
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Smartphone, X, Users, ArrowLeft } from "lucide-react";
import { EnhancedUser } from "./EnhancedUsersSection";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  getDeviceManagementTypeLabel, 
  getDeviceManagementTypeBadgeVariant, 
  getUserIdentityLabel,
  getUserIdentityBadgeVariant,
  type DeviceManagementType 
} from "@/utils/deviceManagementTypes";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../../api";
import { toConvexId } from "@/types/convex";
import { Skeleton } from "@/components/ui/skeleton";
import { getDeviceStatus, getDeviceStatusInfo, getDeviceStatusBadgeColor } from "@/utils/deviceStatus";


interface UserSideViewProps {
  user: EnhancedUser | null;
  isOpen: boolean;
  onClose: () => void;
}

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'active':
      return 'default';
    case 'inactive':
      return 'secondary';
    case 'suspended':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'text-green-600';
    case 'inactive':
      return 'text-gray-600';
    case 'suspended':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

export const UserSideView = ({ user, isOpen, onClose }: UserSideViewProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnToUserId = searchParams.get('returnToUserId');
  
  // Only fetch linked devices if the user ID looks like a real user ID (not a device ID)
  // Device IDs and User IDs have different prefixes in Convex
  const isRealUserId = user?.id && !user.id.startsWith('kn7'); // Device IDs typically start with specific prefixes
  
  const linkedDevices = useConvexQuery(
    api.users.get.byConvexId,
    user && isRealUserId ? { userId: toConvexId.user(user.id) } : "skip"
  );

  const handleDeviceClick = (deviceId: string) => {
    navigate(`/section/devices?deviceId=${deviceId}&returnTo=users&userId=${user?.id}`);
  };

  const handleUserClick = (userId: string) => {
    navigate(`/section/users?userId=${userId}&returnToUserId=${user?.id}`);
  };

  const handleBackToUser = () => {
    if (returnToUserId) {
      navigate(`/section/users?userId=${returnToUserId}`);
    }
  };

  // Extract unique associated users from all devices
  const deviceAssociatedUsers = linkedDevices && linkedDevices.length > 0
    ? linkedDevices.flatMap((device: any) => device.associatedUsers || [])
    : [];
  
  // Add parent to associated users if the user is a child and has a parent
  const allAssociatedUsers = [...deviceAssociatedUsers];
  if (user && user.parent) {
    allAssociatedUsers.push({
      userId: user.parent.id,
      firstName: user.parent.first_name,
      lastName: user.parent.last_name,
      email: user.parent.email,
      identity: 'parent'
    });
  }
  
  // Deduplicate by userId and exclude the current user
  const associatedUsers = Array.from(
    new Map(
      allAssociatedUsers.map((u: any) => [u.userId, u])
    ).values()
  ).filter((u: any) => u.userId !== user?.id);

  if (!user || !isOpen) return null;

  return (
    <>
      {/* Side panel */}
      <div className="h-full w-full bg-background border-l shadow-lg overflow-y-auto">
        <div className="p-6 space-y-4">
        {/* Header with close button */}
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center space-x-3">
            {returnToUserId && (
              <Button variant="ghost" size="icon" onClick={handleBackToUser}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-lg font-semibold">
                {user.first_name} {user.last_name}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <Separator />

        {/* Contact Information Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Contact Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium text-sm py-2">Name</TableCell>
                  <TableCell className="text-sm py-2">{user.first_name} {user.last_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium text-sm py-2">Identity</TableCell>
                  <TableCell className="text-sm py-2">
                    {getUserIdentityLabel(user.device_management_type)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium text-sm py-2">Email</TableCell>
                  <TableCell className="text-sm py-2">{user.email || 'Not available'}</TableCell>
                </TableRow>
                {user.phone && (
                  <TableRow>
                    <TableCell className="font-medium text-sm py-2">Phone</TableCell>
                    <TableCell className="text-sm py-2">{user.phone}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell className="font-medium text-sm py-2">Created</TableCell>
                  <TableCell className="text-sm py-2">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Not available'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Linked Devices */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center space-x-2">
              <Smartphone className="w-4 h-4" />
              <span>Linked Devices</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {!isRealUserId ? (
              <div className="text-sm text-muted-foreground py-2">
                <p>Unable to load linked devices.</p>
                <p className="text-xs mt-1">User profile data is limited.</p>
              </div>
            ) : linkedDevices === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : linkedDevices && linkedDevices.length > 0 ? (
              <div className="space-y-3">
                {linkedDevices.map((device: any) => {
                  const deviceStatus = getDeviceStatus(device.serial, device.jssid);
                  const statusInfo = getDeviceStatusInfo(deviceStatus);
                  
                  return (
                    <div 
                      key={device.deviceId} 
                      className="border rounded-lg p-3 cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => handleDeviceClick(device.deviceId)}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">
                          {device.deviceName || 'Unnamed Device'}
                        </p>
                        <Badge className={getDeviceStatusBadgeColor(deviceStatus) + " text-xs"}>
                          {statusInfo.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-2">No linked devices</div>
            )}
          </CardContent>
        </Card>

        {/* Associated Users */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Associated Users</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {!isRealUserId ? (
              <div className="text-sm text-muted-foreground py-2">
                <p>Unable to load associated users.</p>
              </div>
            ) : linkedDevices === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : associatedUsers.length > 0 ? (
              <div className="space-y-2">
                {associatedUsers.map((associatedUser: any) => (
                  <div 
                    key={associatedUser.userId} 
                    className="border rounded-lg p-3 cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => handleUserClick(associatedUser.userId)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {associatedUser.firstName} {associatedUser.lastName}
                        </p>
                        {associatedUser.email && (
                          <p className="text-xs text-muted-foreground">
                            {associatedUser.email}
                          </p>
                        )}
                      </div>
                      <Badge variant={getUserIdentityBadgeVariant(associatedUser.identity)} className="text-xs">
                        {getUserIdentityLabel(associatedUser.identity)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-2">No associated users</div>
            )}
          </CardContent>
        </Card>

        </div>
      </div>
    </>
  );
};
